/*
 This file is part of GNU Taler
 (C) 2022-2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http://www.gnu.org/licenses/>
 */

import {
  AmountJson,
  AmountString,
  Amounts,
  HttpStatusCode,
  TalerUriAction,
  TalerUris,
  UserAndToken,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import {
  AsyncButton,
  RenderAmount,
  RouteDefinition,
  ShowInputErrorLabel,
  useBankCoreApiContext,
  useNotificationContext,
  useNotifiedOperation,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { VNode, h } from "preact";
import { forwardRef } from "preact/compat";
import { useState } from "preact/hooks";
import { useSettingsContext } from "../context/settings.js";
import { OperationError } from "../components/OperationError.js";
import { useBankState } from "../hooks/bank-state.js";
import { usePreferences } from "../hooks/preferences.js";
import { useSessionState } from "../hooks/session.js";
import { undefinedIfEmpty } from "../utils.js";
import { OperationState } from "./OperationState/index.js";
import { InputAmount, doAutoFocus } from "./PaytoWireTransferForm.js";
import { WithdrawalProgress } from "./WithdrawalProgress.js";
import { IntAmountJson } from "./regional/CreateCashout.js";
import {
  maximumWithdrawalAmount,
  validateWithdrawalAmount,
} from "./withdrawal-amount.js";

const RefAmount = forwardRef(InputAmount);

function parseConfiguredAmount(
  value: string | undefined,
  currency: string,
): AmountJson | undefined {
  if (!value) return undefined;
  const parsed = Amounts.parse(value);
  if (!parsed || parsed.currency.toUpperCase() !== currency.toUpperCase()) {
    return undefined;
  }
  return parsed;
}

export function WithdrawalAmountForm({
  onOperationCreated,
  limit,
  debtLimit,
  focus,
}: {
  limit: IntAmountJson;
  debtLimit: AmountJson;
  focus?: boolean;
  onOperationCreated: (wopid: string) => void;
}): VNode {
  const { i18n } = useTranslationContext();
  const settings = useSettingsContext();
  const [preference, updatePreference] = usePreferences();
  const [, updateBankState] = useBankState();
  const {
    lib: { bank: api },
    config,
    url: backendUrl,
  } = useBankCoreApiContext();
  const { state: credentials } = useSessionState();
  const creds = credentials.status !== "loggedIn" ? undefined : credentials;
  const { showError, displayError } = useNotificationContext();

  const wireFee = parseConfiguredAmount(
    config.wire_transfer_fees,
    limit.currency,
  );
  const minimum = parseConfiguredAmount(
    config.min_wire_transfer_amount,
    limit.currency,
  );
  const configuredMaximum = parseConfiguredAmount(
    config.max_wire_transfer_amount,
    limit.currency,
  );
  const invalidConfiguration =
    (!!config.wire_transfer_fees && !wireFee) ||
    (!!config.min_wire_transfer_amount && !minimum) ||
    (!!config.max_wire_transfer_amount && !configuredMaximum) ||
    (!!minimum &&
      !!configuredMaximum &&
      Amounts.cmp(minimum, configuredMaximum) > 0);
  const fee = wireFee ?? Amounts.zeroOfCurrency(limit.currency);
  const maximum = maximumWithdrawalAmount(limit, fee, configuredMaximum);
  const hasAvailableAmount =
    !Amounts.isZero(maximum) &&
    (!minimum || Amounts.cmp(maximum, minimum) >= 0);

  const initialAmount = `${settings.defaultSuggestedAmount ?? 1}`;
  const parsedInitialAmount = Amounts.parse(
    `${limit.currency}:${initialAmount}`,
  );
  const initialAmountIsValid =
    parsedInitialAmount &&
    !validateWithdrawalAmount(parsedInitialAmount, minimum, maximum);
  const [amountStr, setAmountStr] = useState<string | undefined>(
    initialAmountIsValid ? initialAmount : undefined,
  );
  const [amountTouched, setAmountTouched] = useState(false);

  const trimmedAmountStr = amountStr?.trim();
  const parsedAmount = trimmedAmountStr
    ? Amounts.parse(`${limit.currency}:${trimmedAmountStr}`)
    : undefined;
  const amountValidation = parsedAmount
    ? validateWithdrawalAmount(parsedAmount, minimum, maximum)
    : undefined;
  const amountError = !amountTouched
    ? undefined
    : !trimmedAmountStr
      ? i18n.str`Enter an amount.`
      : !parsedAmount
        ? i18n.str`Enter a valid amount.`
        : amountValidation === "zero"
          ? i18n.str`The amount must be greater than zero.`
          : amountValidation === "below-minimum"
            ? i18n.str`The amount is below the bank's minimum.`
            : amountValidation === "above-maximum"
              ? i18n.str`The amount exceeds what is available to withdraw.`
              : undefined;
  const errors = undefinedIfEmpty({ amount: amountError });
  const totalResult = parsedAmount ? Amounts.add(parsedAmount, fee) : undefined;
  const totalDebit =
    totalResult && !totalResult.saturated ? totalResult.amount : undefined;
  const canSubmit =
    !!creds &&
    !invalidConfiguration &&
    !!parsedAmount &&
    !amountValidation &&
    !!totalDebit;

  // i18n.str`create withdrawal`,
  const start = useNotifiedOperation<
    Awaited<ReturnType<typeof api.createWithdrawal>>,
    [UserAndToken, AmountString]
  >(
    (ct, auth: UserAndToken, amount: AmountString) =>
      api.createWithdrawal(auth, { amount }),
    {
      onSuccess: (success) => {
        const uri = TalerUris.parse(success.taler_withdraw_uri);
        if (uri.tag === "error" || uri.value.type !== TalerUriAction.Withdraw) {
          // Translators: taler://withdraw is a protocol URI, not an email or phone address.
          const title = i18n.str`The server replied with an invalid taler://withdraw URI`;
          // Translators: The placeholder is the malformed taler://withdraw URI returned by the bank.
          const detail = i18n.str`Withdraw URI: ${success.taler_withdraw_uri}`;
          displayError(title, detail);
          return;
        }
        updateBankState("activeWithdrawal", {
          operationId: uri.value.withdrawalOperationId,
          username: creds!.username,
          backendBaseUrl: backendUrl.href,
          confirmationDeferred: false,
          kind: "standard",
        });
        onOperationCreated(uri.value.withdrawalOperationId);
      },
      onFail: showError(i18n.str`Failed to create the withdrawal.`, (fail) => {
        switch (fail.case) {
          case HttpStatusCode.Conflict:
            return i18n.str`The operation was rejected due to insufficient funds`;
          case HttpStatusCode.Unauthorized:
            return i18n.str`Please sign in again.`;
          case HttpStatusCode.NotFound:
            return i18n.str`Account not found`;
          default:
            assertUnreachable(fail);
        }
      }),
    },
  );

  function submit(): Promise<void> | undefined {
    setAmountTouched(true);
    if (!canSubmit || !creds || !parsedAmount) return undefined;
    return start.run(creds, Amounts.stringify(parsedAmount));
  }

  return (
    <section
      class="mx-auto mt-6 max-w-3xl"
      aria-labelledby="wallet-withdrawal-heading"
    >
      <WithdrawalProgress current={1} />
      <h1
        id="wallet-withdrawal-heading"
        class="mt-6 text-xl font-semibold text-brand"
      >
        <i18n.Translate>Withdraw to Taler Wallet</i18n.Translate>
      </h1>
      <p class="mt-2 text-sm leading-6 text-secondary">
        <i18n.Translate>
          Choose an amount. Next, open your wallet and return here to review and
          confirm the withdrawal.
        </i18n.Translate>
      </p>

      {invalidConfiguration ? (
        <div
          class="mt-6 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800"
          role="alert"
        >
          <div class="font-semibold">
            <i18n.Translate>
              Unable to calculate the withdrawal amount
            </i18n.Translate>
          </div>
          <p class="mt-1">
            <i18n.Translate>
              The bank's withdrawal limits or fee are configured incorrectly.
            </i18n.Translate>
          </p>
        </div>
      ) : (
        <form
          class="mt-6 overflow-hidden rounded-xl border border-outlineVariant bg-white shadow-sm"
          autoCapitalize="none"
          autoCorrect="off"
          onSubmit={(event) => {
            event.preventDefault();
            void submit();
          }}
        >
          <div class="p-5 sm:p-6">
            <div class="rounded-lg bg-secondaryContainer px-4 py-4">
              <div class="text-sm font-medium text-secondary">
                <i18n.Translate>Maximum withdrawal</i18n.Translate>
              </div>
              <div class="mt-1 text-2xl font-semibold text-brand">
                <RenderAmount
                  value={maximum}
                  spec={config.currency_specification}
                />
              </div>
              <div class="mt-3 flex items-baseline justify-between gap-4 border-t border-secondary/20 pt-3 text-sm text-secondary">
                <span class="font-medium">
                  <i18n.Translate>Debt limit</i18n.Translate>
                </span>
                <span class="font-semibold">
                  <RenderAmount
                    value={debtLimit}
                    spec={config.currency_specification}
                  />
                </span>
              </div>
              <p class="mt-1 text-xs text-secondary">
                {Amounts.isZero(debtLimit) ? (
                  <i18n.Translate>
                    This is not your account balance. This account cannot go
                    into debt.
                  </i18n.Translate>
                ) : (
                  <i18n.Translate>
                    This is not your account balance. It includes borrowing up
                    to the debt limit shown here.
                  </i18n.Translate>
                )}
              </p>
            </div>

            {!hasAvailableAmount ? (
              <div
                class="mt-4 rounded-lg border border-warning/60 bg-warningContainer px-4 py-3 text-sm text-onWarningContainer"
                role="status"
              >
                <i18n.Translate>
                  There is not enough available balance to cover a withdrawal
                  and the bank fee.
                </i18n.Translate>
              </div>
            ) : undefined}

            <div class="mt-6 max-w-sm">
              <div
                id="withdraw-amount-label"
                class="text-sm font-medium text-onBackground"
              >
                <i18n.Translate>Amount to your Taler Wallet</i18n.Translate>
              </div>
              <RefAmount
                currency={limit.currency}
                value={amountStr}
                name="withdraw-amount"
                ariaLabelledby="withdraw-amount-label"
                onChange={
                  hasAvailableAmount
                    ? (value) => {
                        setAmountStr(value);
                        setAmountTouched(true);
                      }
                    : undefined
                }
                ref={focus ? doAutoFocus : undefined}
              />
              <ShowInputErrorLabel
                message={errors?.amount}
                isDirty={amountTouched}
              />
              {!Amounts.isZero(maximum) ? (
                <button
                  type="button"
                  class="mt-2 text-sm font-medium text-primaryDark hover:underline"
                  onClick={() => {
                    setAmountStr(Amounts.stringifyValue(maximum));
                    setAmountTouched(true);
                  }}
                >
                  <i18n.Translate>Use maximum available</i18n.Translate>
                </button>
              ) : undefined}
              {minimum && !Amounts.isZero(minimum) ? (
                <p class="mt-2 text-xs text-secondary">
                  <i18n.Translate>Minimum amount</i18n.Translate>:{" "}
                  <RenderAmount
                    value={minimum}
                    spec={config.currency_specification}
                  />
                </p>
              ) : undefined}
            </div>

            <dl class="mt-6 divide-y divide-onBackground/10 text-sm">
              <div class="flex items-center justify-between gap-4 py-3">
                <dt class="text-secondary">
                  <i18n.Translate>Wallet receives</i18n.Translate>
                </dt>
                <dd class="font-medium text-onBackground">
                  {parsedAmount ? (
                    <RenderAmount
                      value={parsedAmount}
                      spec={config.currency_specification}
                    />
                  ) : (
                    <span aria-hidden="true">—</span>
                  )}
                </dd>
              </div>
              <div class="flex items-center justify-between gap-4 py-3">
                <dt class="text-secondary">
                  <i18n.Translate>Bank fee</i18n.Translate>
                </dt>
                <dd class="font-medium text-onBackground">
                  {Amounts.isZero(fee) ? (
                    <i18n.Translate>No fee</i18n.Translate>
                  ) : (
                    <RenderAmount
                      value={fee}
                      spec={config.currency_specification}
                    />
                  )}
                </dd>
              </div>
              <div class="flex items-center justify-between gap-4 py-3 text-base">
                <dt class="font-semibold text-onBackground">
                  <i18n.Translate>Total debited</i18n.Translate>
                </dt>
                <dd class="font-semibold text-onBackground">
                  {totalDebit ? (
                    <RenderAmount
                      value={totalDebit}
                      spec={config.currency_specification}
                    />
                  ) : (
                    <span aria-hidden="true">—</span>
                  )}
                </dd>
              </div>
            </dl>

            <p class="mt-4 text-sm leading-6 text-secondary">
              <i18n.Translate>
                No money moves until you review and confirm the withdrawal.
              </i18n.Translate>
            </p>

            {preference.showInstallWallet ? (
              <div class="mt-4 flex items-start justify-between gap-4 border-t border-onBackground/10 pt-4 text-sm text-secondary">
                <p>
                  <i18n.Translate>Need a Taler Wallet?</i18n.Translate>{" "}
                  <a
                    target="_blank"
                    data-name="wallet page"
                    rel="noreferrer noopener"
                    class="font-semibold text-primaryDark hover:underline"
                    href="https://taler.net/en/wallet.html"
                  >
                    <i18n.Translate>Get the wallet</i18n.Translate>
                  </a>
                </p>
                <button
                  type="button"
                  class="shrink-0 text-secondary hover:text-onBackground"
                  aria-label={i18n.str`Dismiss wallet installation hint`}
                  onClick={() => updatePreference("showInstallWallet", false)}
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
            ) : undefined}
          </div>

          <div class="border-t border-onBackground/10 bg-background px-5 py-4 sm:px-6">
            <OperationError class="mb-4" />
            <div class="flex justify-end">
              <AsyncButton
                submit
                name="continue"
                class="w-full cursor-pointer rounded-md bg-primary px-4 py-2 text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white disabled:cursor-default disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary sm:w-auto"
                disabled={!canSubmit}
                onClick={(event) => {
                  event.preventDefault();
                  return submit();
                }}
              >
                <i18n.Translate>Continue to wallet</i18n.Translate>
              </AsyncButton>
            </div>
          </div>
        </form>
      )}
    </section>
  );
}

export function WalletWithdrawForm({
  focus,
  limit,
  debtLimit,
  routeCancel,
  onOperationCreated,
  onOperationAborted,
}: {
  limit: IntAmountJson;
  debtLimit: AmountJson;
  focus?: boolean;
  onOperationCreated: (wopid: string) => void;
  onOperationAborted: () => void;
  routeCancel: RouteDefinition;
}): VNode {
  const [preference] = usePreferences();
  const [bankState] = useBankState();
  const { state: credentials } = useSessionState();
  const { url: backendUrl } = useBankCoreApiContext();
  const activeWithdrawal = bankState.activeWithdrawal;
  const activeOperationId =
    activeWithdrawal &&
    credentials.status === "loggedIn" &&
    activeWithdrawal.username === credentials.username &&
    activeWithdrawal.backendBaseUrl === backendUrl.href
      ? activeWithdrawal.operationId
      : undefined;

  if (activeOperationId) {
    return <div />;
  }

  return preference.fastWithdrawalForm ? (
    <OperationState
      creationMode="suggested"
      focus={focus}
      routeClose={routeCancel}
      onAbort={onOperationAborted}
      onOperationCreated={onOperationCreated}
    />
  ) : (
    <WithdrawalAmountForm
      focus={focus}
      limit={limit}
      debtLimit={debtLimit}
      onOperationCreated={onOperationCreated}
    />
  );
}
